/**
 * Who is signed in, which company they are working in, and what their role
 * there lets them do. Sign-in is two steps when the account has MFA on.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, setAccessToken, setUnauthenticatedHandler } from './api';

export interface SessionUser {
  id: string;
  email: string;
  name: string;
  isPlatformAdmin: boolean;
  mustResetPassword: boolean;
  mfaEnabled: boolean;
  companyId: string | null;
  companyName: string | null;
  roleName: string | null;
  permissions: string[];
  /** Restricted modules granted to this person (vaw.access, badac.access, health.access). */
  grants: string[];
  /** M14: the advisor firm this person works for. */
  firm?: { id: string; name: string; role: 'PARTNER' | 'STAFF' } | null;
  /** The current company is a sandbox copy (M15-17). */
  sandbox?: boolean;
  /** Set while this is an ITCSI support session (M02-07). */
  supportSessionId?: string | null;
  supportEndsAt?: string | null;
}

export interface CompanyChoice {
  companyId: string;
  companyName: string;
  roleName: string;
  expiresAt: string | null;
}

interface SessionResponse {
  accessToken: string;
  user: SessionUser;
}

export type SignInResult = { kind: 'session'; user: SessionUser } | { kind: 'mfa'; mfaToken: string };

interface AuthState {
  user: SessionUser | null;
  companies: CompanyChoice[];
  loading: boolean;
  signIn: (email: string, password: string) => Promise<SignInResult>;
  verifyMfa: (mfaToken: string, code: string) => Promise<SessionUser>;
  adoptSession: (accessToken: string, user: SessionUser) => void;
  switchCompany: (companyId: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
  can: (...permissions: string[]) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [companies, setCompanies] = useState<CompanyChoice[]>([]);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  const loadMe = useCallback(async () => {
    const me = await api<{ user: SessionUser; companies: CompanyChoice[] }>('/auth/me');
    setUser(me.user);
    setCompanies(me.companies);
  }, []);

  const adoptSession = useCallback(
    (accessToken: string, next: SessionUser) => {
      setAccessToken(accessToken);
      setUser(next);
      void loadMe().catch(() => undefined);
    },
    [loadMe],
  );

  // A valid refresh cookie means the session survives a page reload.
  useEffect(() => {
    let cancelled = false;
    setUnauthenticatedHandler(() => setUser(null));
    void (async () => {
      try {
        const response = await fetch('/api/v1/auth/refresh', { method: 'POST', credentials: 'include' });
        if (response.ok) {
          const body = (await response.json()) as SessionResponse;
          setAccessToken(body.accessToken);
          if (!cancelled) {
            setUser(body.user);
            await loadMe().catch(() => undefined);
          }
        }
      } catch {
        // No session to resume; the login page handles it.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      setUnauthenticatedHandler(null);
    };
  }, [loadMe]);

  const signIn = useCallback(
    async (email: string, password: string): Promise<SignInResult> => {
      const body = await api<SessionResponse | { mfaRequired: true; mfaToken: string }>('/auth/login', {
        method: 'POST',
        body: { email, password },
        anonymous: true,
      });
      if ('mfaRequired' in body) return { kind: 'mfa', mfaToken: body.mfaToken };
      adoptSession(body.accessToken, body.user);
      return { kind: 'session', user: body.user };
    },
    [adoptSession],
  );

  const verifyMfa = useCallback(
    async (mfaToken: string, code: string) => {
      const body = await api<SessionResponse>('/auth/mfa/verify', { method: 'POST', body: { mfaToken, code }, anonymous: true });
      adoptSession(body.accessToken, body.user);
      return body.user;
    },
    [adoptSession],
  );

  const switchCompany = useCallback(
    async (companyId: string) => {
      const body = await api<SessionResponse>('/auth/switch-company', { method: 'POST', body: { companyId } });
      queryClient.clear();
      adoptSession(body.accessToken, body.user);
    },
    [adoptSession, queryClient],
  );

  const signOut = useCallback(async () => {
    try {
      await api('/auth/logout', { method: 'POST' });
    } finally {
      setAccessToken(null);
      setUser(null);
      setCompanies([]);
      queryClient.clear();
    }
  }, [queryClient]);

  const value = useMemo<AuthState>(
    () => ({
      user,
      companies,
      loading,
      signIn,
      verifyMfa,
      adoptSession,
      switchCompany,
      signOut,
      refreshUser: loadMe,
      can: (...permissions: string[]) => Boolean(user && permissions.some((key) => user.permissions.includes(key))),
    }),
    [user, companies, loading, signIn, verifyMfa, adoptSession, switchCompany, signOut, loadMe],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}
